import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';

type Props = {
  title: string;
  vehicleClassIds?: number[];
  navigateTo: string;
  vehicle: any[];
};

const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
  }).format(price);

const VehicleSoldTag = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      right: 0,
      bgcolor: 'warning.main',
      color: 'black',
      px: 1.5,
      py: 0.5,
      fontWeight: 'bold',
      fontSize: 12,
      borderTopRightRadius: 8,
      borderBottomLeftRadius: 8,
      zIndex: 2,
    }}
  >
    SOLD
  </Box>
);

const VehicleViewCountTag = ({ count }: { count: number }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 8,
      left: 8,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      color: '#fff',
      px: 1,
      py: 0.25,
      borderRadius: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: 12,
      zIndex: 1,
    }}
  >
    <VisibilityIcon sx={{ fontSize: 14 }} />
    {count.toLocaleString()}
  </Box>
);

const VehicleExploreSection = ({ title, vehicleClassIds, navigateTo, vehicle }: Props) => {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [rawVehicles, setRawVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVehicles(vehicle);
    setRawVehicles(vehicle);
    setLoading(false); // data received
  }, [vehicle]);


  useEffect(() => {
    const sorted = [...rawVehicles];
    if (tabIndex === 0) {
      sorted.sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0));
    } else if (tabIndex === 1) {
      sorted.sort((a, b) => new Date(b.addDate).getTime() - new Date(a.addDate).getTime());
    }
    setVehicles(sorted);
  }, [tabIndex, rawVehicles]);


  useEffect(() => {
    if (scrollRef.current && vehicles.length > 0) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({ left: 0, behavior: 'auto' });
        });
      }, 0);
    }
  }, [vehicles]);

  const scrollBy = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  };

  const formatDate = (date?: string) =>
    date ? dayjs(date).format('DD/MM/YYYY') : '';

  return (
    <Box
      sx={{ py: 5, px: { xs: 2, md: 0 } }}
      maxWidth="lg"
      mx="auto" // This centers the container like the hero section
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        <Button size="small" onClick={() => navigate(navigateTo)}>
          View All
        </Button>
      </Box>

      <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)} sx={{ mb: 3 }}>
        <Tab label="Popular" />
        <Tab label="Latest" />
      </Tabs>

      <Box sx={{ position: 'relative' }}>
        <Button
          onClick={() => scrollBy(-300)}
          sx={{
            position: 'absolute',
            top: '40%',
            left: -20,
            zIndex: 3,
            minWidth: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: 2,
            display: { xs: 'none', md: 'flex' },
          }}
        >
          <ChevronLeftIcon />
        </Button>

        <Box
          ref={scrollRef}
          sx={{
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            pb: 4,
            overflowX: 'auto',
            display: 'flex',
            gap: 2,
            scrollSnapType: 'x mandatory',
            px: 1,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ccc',
              borderRadius: 4,
            },
          }}
        >
          {(loading ? [...Array(4)] : vehicles.slice(0, 10)).map((vehicle, i) =>
            loading || !vehicle ? (
              <Box key={i} sx={{ flex: '0 0 auto', width: isSmallScreen ? 300 : 280 }}>
                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
              </Box>
            ) : (
              <Box
                key={vehicle.vehicleId}
                sx={{
                  flex: '0 0 auto',
                  width: isSmallScreen ? 300 : 280,
                  scrollSnapAlign: 'start',
                }}
              >
                <RouterLink
                  to={`/vehicle-details/${vehicle.vehicleId}`}
                  style={{ textDecoration: 'none' }}
                >
                  <Card
                    component="a"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': { boxShadow: 4 },
                    }}
                  >
                    {vehicle?.statusId === 'VSL' && <VehicleSoldTag />}

                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4 / 3',
                        overflow: 'hidden',
                        backgroundColor: '#f5f5f5',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="img"
                        src={vehicle.vehicleImageUrl || 'https://via.placeholder.com/1024x768?text=No+Image'}
                        alt={vehicle.vehicleModel || 'Vehicle Image'}
                        loading="lazy" // ✅ this is the lazy load!
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />

                      {/* Premium Ad Tag */}
                      {vehicle?.racingAd && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,   // 👈 moved from left: 8 to right: 8
                            bgcolor: 'warning.main',
                            color: 'warning.contrastText',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            boxShadow: 1,
                          }}
                        >
                          Racing Ad
                        </Box>
                      )}

                      {vehicle.viewCount != null && <VehicleViewCountTag count={vehicle.viewCount} />}
                    </Box>

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                        {formatPrice(vehicle.askingPrice)}
                      </Typography>

                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {vehicle.vehicleBrand} {vehicle.vehicleModelName}
                      </Typography>


                      <Typography variant="caption" color="text.secondary">
                        {[
                          vehicle.firstRegistrationDate && formatDate(vehicle.firstRegistrationDate),
                          vehicle.mileage && `${vehicle.mileage.toLocaleString()} km`,
                          vehicle.vehicleBodyType,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '-'}
                      </Typography>
                      {vehicle?.showDealerInVehicleList && (
                        <Typography variant="caption" color="#70287f" fontWeight={500} sx={{ display: 'block', mt: 0.5 }}>
                          {vehicle?.dealerName}
                        </Typography>

                      )}

                    </CardContent>
                  </Card>
                </RouterLink>
              </Box>
            )
          )}
        </Box>

        <Button
          onClick={() => scrollBy(300)}
          sx={{
            position: 'absolute',
            top: '40%',
            right: -20,
            zIndex: 3,
            minWidth: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: 2,
            display: { xs: 'none', md: 'flex' },
          }}
        >
          <ChevronRightIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default VehicleExploreSection;
